import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { connect } from 'react-redux'
import { initial, range } from 'lodash'
import { Panel } from 'react-bootstrap'
import DateSelect from 'components/date-select'
import { StyledSubHeading } from 'components/headings'
import { Checkbox, Form } from 'components/forms'
import { SaveButton } from 'components/buttons'
import { successMessage } from 'lib/flash-message'

import { today } from 'lib/date'
import { createValidator, required } from 'lib/form-validation'

export const EmailPreferencesForm = ({ submitData, initialValues, user }) => {
  const [submitting, setSubmitting] = useState(false)
  const { errors, handleSubmit, register, reset, watch, setValue, getValues } =
    useForm(initialValues)
  const [arrivalDate, setArrivalDate] = useState(
    initialValues.user?.family?.arrivalDate
  )

  const unsubscribeFromAll = (e) => {
    const { checked } = e.target
    const fields = getValues()
    Object.keys(fields).forEach((key) =>
      checked ? setValue(key, false) : reset(key)
    )
  }

  const renderPregnancyWeeklyForm = () => {
    if (watch('pregnancyWeekly')) {
      const currentYear = today.getFullYear()

      return (
        <Panel className="panel-bg-muted">
          <Panel.Body>
            <div className="pas">
              <div className="h6 text-bold mvn">Bedazzle your emails ✨</div>
              <p className="h6 mvs">
                Personalize your emails by entering your arrival date.
              </p>
              <div style={{ maxWidth: 400 }}>
                <DateSelect
                  label="Arrival Date"
                  value={arrivalDate}
                  years={range(currentYear - 1, currentYear + 2).reverse()}
                  onChange={(e) => setArrivalDate(e)}
                />
              </div>
            </div>
          </Panel.Body>
        </Panel>
      )
    }
  }

  const onSubmit = (emailPreferences) => {
    const data = {
      user: {
        emailPreferences: { ...emailPreferences },
        family: {
          arrivalDate,
        },
      },
    }
    submitData(data).then(() => {
      successMessage('Your communication preferences have been updated!')
    })
  }

  return (
    <div>
      <StyledSubHeading>Communication Preferences</StyledSubHeading>

      <Form onSubmit={handleSubmit(onSubmit)}>
        {user.isGiftGiverOnly ? null : (
          <div>
            <div className="h6">
              Eeny, meeny, miny, moe… Pick the very best emails that will help
              your family grow.
            </div>

            <div className="mbxl">
              <Checkbox
                defaultChecked={
                  initialValues.user.emailPreferences.pregnancyWeekly
                }
                help="Personalized emails about your body, your baby and what you really need to know."
                inputRef={register}
                label={<b>Week-by-Week Pregnancy and Parenting Emails </b>}
                name="pregnancyWeekly"
              />

              <Checkbox
                defaultChecked={initialValues.user.emailPreferences.newsletter}
                help="A once-a-week round-up of news and noteworthy stories from around the web."
                inputRef={register}
                label={<b>Hello Baby Weekly Newsletter</b>}
                name="newsletter"
              />
              <Checkbox
                defaultChecked={
                  initialValues.user.emailPreferences.babyRegistry101
                }
                help="Expert tips on how to build your baby registry and find gear for your baby's next stage, plus all the best sales."
                inputRef={register}
                label={<b>Registry & Shopping Emails</b>}
                name="babyRegistry101"
              />
              <Checkbox
                defaultChecked={initialValues.user.emailPreferences.sponsored}
                help="Exclusive deals and information sent from Babylist on behalf of our partners."
                inputRef={register}
                label={<b>Partner Emails</b>}
                name="sponsored"
              />
              <Checkbox
                defaultChecked={
                  initialValues.user.emailPreferences.announcements
                }
                help="Changes to your registry, surveys and occasional updates about new Babylist features."
                inputRef={register}
                label={<b>Announcements</b>}
                name="announcements"
              />
            </div>
          </div>
        )}

        {renderPregnancyWeeklyForm()}

        {user.isRegistryUser ? (
          <div>
            <StyledSubHeading className="h5">Registry Alerts</StyledSubHeading>
            <div className="mbl">
              <Checkbox
                defaultChecked={
                  initialValues.user.emailPreferences.reservationAlerts
                }
                help="Get notified when someone purchases a gift from your registry."
                inputRef={register}
                label={<b>Purchase Alerts</b>}
                name="reservationAlerts"
              />
              <Checkbox
                defaultChecked={
                  initialValues.user.emailPreferences.priceStockAlerts
                }
                help="Get notified when there are price or stock changes for items on your registry."
                inputRef={register}
                label={<b>Price & Stock Alerts</b>}
                name="priceStockAlerts"
              />
            </div>
          </div>
        ) : null}

        {user.isGiftGiver ? (
          <div>
            <StyledSubHeading className="h5">
              Gift Giver Emails
            </StyledSubHeading>
            <div className="mbl">
              <Checkbox
                defaultChecked={
                  initialValues.user.emailPreferences.giftGiverGiftAdvice
                }
                help="Age/stage appropriate clothes, toys and gear recommendations (plus deal alerts!) to help you give the best gifts. "
                inputRef={register}
                label={<b>Gift Advice</b>}
                name="giftGiverGiftAdvice"
              />
              <Checkbox
                defaultChecked={
                  initialValues.user.emailPreferences.giftGiverPartnerEmail
                }
                help="Exclusive deals and information sent from Babylist on behalf of our partners"
                inputRef={register}
                label={<b>Partner Emails</b>}
                name="giftGiverPartnerEmail"
              />
            </div>
          </div>
        ) : null}

        <hr className="mbl" />
        <Checkbox
          label={<b>Unsubscribe From All</b>}
          onChange={unsubscribeFromAll}
        />

        <hr className="mbl mtl" />
        <SaveButton pill submitting={submitting}>
          Save Changes
        </SaveButton>
      </Form>
    </div>
  )
}

const dueDateValidator = createValidator({
  'user.family.arrivalDate': [required],
})

const validate = (data) => {
  if (data.user.emailPreferences.pregnancyWeekly) {
    return dueDateValidator(data)
  }
  return {}
}

const mapStateToProps = (state, ownProps) => ({
  ...ownProps,
  initialValues: {
    user: {
      emailPreferences: state.user.emailPreferences,
      family: state.user.family,
      isGiftGiver: state.user.isGiftGiver,
      giftGiverRegistries: state.user.giftGiverRegistries,
    },
  },
  user: {
    email: state.user.email,
    isRegistryUser: state.user.registries.length !== 0,
    isGiftGiver: state.user.isGiftGiver,
    isGiftGiverOnly: state.user.isGiftGiverOnly,
    giftGiverRegistries: state.user.giftGiverRegistries,
  },
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  submitData: (data) => ownProps._handleSubmit(data, dispatch),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmailPreferencesForm)
